import React, { useState } from 'react';
import '../styles/DesignerPage.css';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area } from 'recharts';

function DesignerPage() {
  const [optimizeDensityParams, setOptimizeDensityParams] = useState({
    yield_strength_lb: 300.0,
    max_iter: 200,
    num_runs: 3,
    neighbor_size: 2,
  });
  const [optimizeYieldStrengthParams, setOptimizeYieldStrengthParams] = useState({
    density_ub: 4.5,
    max_iter: 200,
    num_runs: 3,
    neighbor_size: 2,
  });
  const [optimizeElongationUtsParams, setOptimizeElongationUtsParams] = useState({
    density_ub: 4.5,
    max_iter: 200,
    num_runs: 3,
    neighbor_size: 2,
  });
  const [optimizeSpecificStrengthParams, setOptimizeSpecificStrengthParams] = useState({
    elongation_lb: 5.0,
    density_ub: 4.5,
    max_iter: 200,
    num_runs: 3,
    neighbor_size: 2,
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);

  const [densityResults, setDensityResults] = useState(null);
  const [yieldStrengthResults, setYieldStrengthResults] = useState(null);
  const [elongationUtsResults, setElongationUtsResults] = useState(null);
  const [specificStrengthResults, setSpecificStrengthResults] = useState(null);
  const [isSearchingDensity, setIsSearchingDensity] = useState(false);
  const [isSearchingYieldStrength, setIsSearchingYieldStrength] = useState(false);
  const [isSearchingElongationUts, setIsSearchingElongationUts] = useState(false);
  const [isSearchingSpecificStrength, setIsSearchingSpecificStrength] = useState(false);
  const [error, setError] = useState(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const handleDensityParamChange = (param, value) => {
    setOptimizeDensityParams(prev => ({ ...prev, [param]: parseFloat(value) || 0 }));
  };
  const handleYieldStrengthParamChange = (param, value) => {
    setOptimizeYieldStrengthParams(prev => ({ ...prev, [param]: parseFloat(value) || 0 }));
  };
  const handleElongationUtsParamChange = (param, value) => {
    setOptimizeElongationUtsParams(prev => ({ ...prev, [param]: parseFloat(value) || 0 }));
  };
  const handleSpecificStrengthParamChange = (param, value) => {
    setOptimizeSpecificStrengthParams(prev => ({ ...prev, [param]: parseFloat(value) || 0 }));
  };

  const handleOptimizeDensity = async () => {
    setIsSearchingDensity(true);
    setError(null);
    setDensityResults(null);
    try {
      const res = await fetch('/api/design/optimize-density', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(optimizeDensityParams)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Search request failed');
      setDensityResults(await res.json());
    } catch (e) { setError(e.message); } finally { setIsSearchingDensity(false); }
  };

  const handleOptimizeYieldStrength = async () => {
    setIsSearchingYieldStrength(true);
    setError(null);
    setYieldStrengthResults(null);
    const urls = [
      '/api/design/optimize-yield-strength',
      '/design/optimize-yield-strength',
      'http://localhost:8001/design/optimize-yield-strength',
    ];
    try {
      let lastErr = null;
      for (const url of urls) {
        try {
          const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(optimizeYieldStrengthParams) });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            lastErr = new Error(data.detail || `Request failed (${res.status}) at ${url}`);
            continue;
          }
          const json = await res.json();
          setYieldStrengthResults(json);
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          continue;
        }
      }
      if (lastErr) throw lastErr;
    } catch (e) {
      setError(e.message);
    } finally {
      setIsSearchingYieldStrength(false);
    }
  };

  const handleOptimizeElongationUts = async () => {
    setIsSearchingElongationUts(true);
    setError(null);
    setElongationUtsResults(null);
    try {
      const res = await fetch('/api/design/optimize-elongation-uts-product', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(optimizeElongationUtsParams)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Search request failed');
      setElongationUtsResults(await res.json());
    } catch (e) { setError(e.message); } finally { setIsSearchingElongationUts(false); }
  };

  const handleOptimizeSpecificStrength = async () => {
    setIsSearchingSpecificStrength(true);
    setError(null);
    setSpecificStrengthResults(null);
    try {
      const res = await fetch('/api/design/optimize-specific-strength', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(optimizeSpecificStrengthParams)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Search request failed');
      setSpecificStrengthResults(await res.json());
    } catch (e) { setError(e.message); } finally { setIsSearchingSpecificStrength(false); }
  };

  const renderResultsTable = (results, title) => {
    if (!results || !results.results) return null;
    const top = results.results.slice(0, 5);
    return (
      <div className="search-results">
        <h3>{title}</h3>
        <div className="results-table">
          <table>
            <thead>
              <tr>
                <th>rank</th><th>composition (at.%)</th><th>density (g/cm³)</th><th>yield strength (MPa)</th><th>ultimate strength (MPa)</th><th>elongation (%)</th><th>elongation × UTS</th><th>specific strength (MPa·cm³/g)</th>
              </tr>
            </thead>
            <tbody>
              {top.map((r, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td><div className="composition-cell">{Object.entries(r.composition).map(([el, v]) => `${el}: ${v.toFixed(2)}%`).join(', ')}</div></td>
                  <td>{r.density.toFixed(3)}</td>
                  <td>{r.yield_strength.toFixed(2)}</td>
                  <td>{r.uts.toFixed(2)}</td>
                  <td>{r.elongation.toFixed(2)}</td>
                  <td>{(r.elongation / 100 * r.uts).toFixed(2)}</td>
                  <td>{r.specific_strength.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderOptimizationChart = (results, title, type) => {
    if (!results || !results.best_score_history) return null;
    const history = results.best_score_history;
    let chartData, yAxisLabel, lineColor, gradientId;
    
    if (type === 'density') { 
      chartData = history.map((s, i) => ({ iteration: i + 1, value: -s })); 
      yAxisLabel = 'Density'; 
      lineColor = '#3b82f6'; 
      gradientId = 'densityGradient';
    }
    else if (type === 'elongation_uts') { 
      chartData = history.map((s, i) => ({ iteration: i + 1, value: s })); 
      yAxisLabel = 'Elongation × UTS'; 
      lineColor = '#0ea5e9'; 
      gradientId = 'elongationGradient';
    }
    else if (type === 'specific_strength') { 
      chartData = history.map((s, i) => ({ iteration: i + 1, value: s })); 
      yAxisLabel = 'Specific Strength'; 
      lineColor = '#10b981'; 
      gradientId = 'strengthGradient';
    }
    else if (type === 'yield_strength') { 
      chartData = history.map((s, i) => ({ iteration: i + 1, value: s })); 
      yAxisLabel = 'Yield Strength'; 
      lineColor = '#ef4444'; 
      gradientId = 'yieldGradient';
    }
    
    const vals = chartData.map(d => d.value);
    const minV = Math.min(...vals), maxV = Math.max(...vals);
    
    // 根据优化类型确定最优值（密度优化找最小值，其他找最大值）
    const bestValue = type === 'density' ? Math.min(...vals) : Math.max(...vals);
    const bestIndex = vals.indexOf(bestValue);
    const pad = Math.max((maxV - minV) * 0.1, maxV * 0.01);
    const domain = [Math.max(0, minV - pad), maxV + pad];
    
    return (
      <div className="score-chart enhanced-chart">
        <h4>{title}</h4>
        <ResponsiveContainer width="100%" height={450}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={lineColor} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={lineColor} stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" strokeOpacity={0.6} />
            <XAxis 
              dataKey="iteration" 
              tick={{ fontSize: 12, fill: '#6b7280' }}
              axisLine={{ stroke: '#d1d5db' }}
              tickLine={{ stroke: '#d1d5db' }}
            />
            <YAxis 
              domain={domain} 
              tick={{ fontSize: 12, fill: '#6b7280' }}
              axisLine={{ stroke: '#d1d5db' }}
              tickLine={{ stroke: '#d1d5db' }}
              tickFormatter={v => v.toFixed(3)}
            />
            <Tooltip 
              formatter={val => {
                let label; 
                if (type === 'density') label = 'Density (g/cm³)'; 
                else if (type === 'specific_strength') label = 'Specific Strength (MPa·cm³/g)'; 
                else if (type === 'yield_strength') label = 'Yield Strength (MPa)'; 
                else label = 'Elongation × UTS';
                return [Number(val).toFixed(4), label];
              }} 
              labelFormatter={l => `Iteration ${l}`}
              contentStyle={{
                backgroundColor: '#ffffff',
                border: `2px solid ${lineColor}`,
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Legend 
              verticalAlign="bottom" 
              height={36} 
              wrapperStyle={{ paddingTop: '15px', fontSize: '14px', fontWeight: '500' }}
              formatter={() => {
                if (type === 'density') return 'Density (g/cm³)'; 
                if (type === 'specific_strength') return 'Specific Strength (MPa·cm³/g)'; 
                if (type === 'yield_strength') return 'Yield Strength (MPa)'; 
                return 'Elongation × UTS';
              }}
            />
            {/* 填充区域 */}
            <defs>
              <linearGradient id={`${gradientId}Fill`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={lineColor} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={lineColor} stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="value"
              stroke="none"
              fill={`url(#${gradientId}Fill)`}
            />
            {/* 主线条 */}
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={lineColor} 
              strokeWidth={3}
              dot={false}
              activeDot={{ 
                r: 6, 
                stroke: lineColor, 
                strokeWidth: 3, 
                fill: '#ffffff',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
              }}
              name={yAxisLabel}
            />
            {/* 最优点标记 */}
            <Line
              type="monotone"
              dataKey="value"
              stroke="none"
              dot={(props) => {
                const { cx, cy, index } = props;
                if (index === bestIndex) {
                  return (
                    <g>
                      <circle 
                        cx={cx} 
                        cy={cy} 
                        r={8} 
                        fill={lineColor} 
                        stroke="#ffffff" 
                        strokeWidth={3}
                        filter="drop-shadow(0 2px 8px rgba(0,0,0,0.3))"
                      />
                      <circle cx={cx} cy={cy} r={4} fill="#ffffff" />
                    </g>
                  );
                }
                return null;
              }}
            />
          </LineChart>
        </ResponsiveContainer>
        <div className="chart-summary">
          <span className="best-value">
            {type === 'density' ? 'Minimum' : 'Maximum'} Value: <strong>{bestValue.toFixed(4)}</strong> at iteration {bestIndex + 1}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="designer-page">
      <div className="designer-container">
        <div className="designer-header">
          <h2>High-Entropy Alloy Designer</h2>
          <p className="designer-subtitle">Multi-objective optimization for high-performance alloy design</p>
        </div>

        <div className="optimization-cards">
          <div className="optimization-card">
            <h3>Minimize Density</h3>
            <p>Find alloys with minimum density while satisfying a minimum yield strength constraint.</p>
            <div className="card-params">
              <div className="param-group">
                <label>Minimum Yield Strength (MPa)</label>
                <input type="number" value={optimizeDensityParams.yield_strength_lb} onChange={e => handleDensityParamChange('yield_strength_lb', e.target.value)} min="0" step="10" />
              </div>
            </div>
            <button className={`card-button minimize-button ${isSearchingDensity ? 'searching-dots' : ''}`} onClick={handleOptimizeDensity} disabled={isSearchingDensity}>
              {isSearchingDensity ? 'Searching' : 'Minimize Density'}
            </button>
          </div>

          <div className="optimization-card">
            <h3>Maximize Yield Strength</h3>
            <p>Find alloys with maximum yield strength while satisfying a maximum density constraint.</p>
            <div className="card-params">
              <div className="param-group">
                <label>Maximum Density (g/cm³)</label>
                <input type="number" value={optimizeYieldStrengthParams.density_ub} onChange={e => handleYieldStrengthParamChange('density_ub', e.target.value)} min="0" step="0.1" />
              </div>
            </div>
            <button className={`card-button maximize-button ${isSearchingYieldStrength ? 'searching-dots' : ''}`} onClick={handleOptimizeYieldStrength} disabled={isSearchingYieldStrength}>
              {isSearchingYieldStrength ? 'Searching' : 'Maximize Yield Strength'}
            </button>
          </div>
        </div>

        {/* 高级设置 */}
        <div className="advanced-settings-section">
          <button className="advanced-toggle" onClick={() => setShowAdvanced(!showAdvanced)} type="button">
            <span className="toggle-icon">{showAdvanced ? '▼' : '▶'}</span>
            Advanced Settings
          </button>
          {showAdvanced && (
            <div className="advanced-params">
              <div className="advanced-grid">
                <div className="param-group-compact">
                  <label>Max Iterations</label>
                  <input type="number" value={optimizeDensityParams.max_iter} onChange={e => { const v = e.target.value; handleDensityParamChange('max_iter', v); handleYieldStrengthParamChange('max_iter', v); handleElongationUtsParamChange('max_iter', v); handleSpecificStrengthParamChange('max_iter', v); }} min="100" step="100" />
                  <span className="param-hint-compact">Higher values find better solutions</span>
                </div>
                <div className="param-group-compact">
                  <label>Number of Runs</label>
                  <input type="number" value={optimizeDensityParams.num_runs} onChange={e => { const v = e.target.value; handleDensityParamChange('num_runs', v); handleYieldStrengthParamChange('num_runs', v); handleElongationUtsParamChange('num_runs', v); handleSpecificStrengthParamChange('num_runs', v); }} min="1" max="20" step="1" />
                  <span className="param-hint-compact">Multiple runs for better results</span>
                </div>
                <div className="param-group-compact">
                  <label>Neighbor Size</label>
                  <input type="number" value={optimizeDensityParams.neighbor_size} onChange={e => { const v = e.target.value; handleDensityParamChange('neighbor_size', v); handleYieldStrengthParamChange('neighbor_size', v); handleElongationUtsParamChange('neighbor_size', v); handleSpecificStrengthParamChange('neighbor_size', v); }} min="1" max="6" step="1" />
                  <span className="param-hint-compact">Size of search neighborhood (affects quality vs speed)</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 旧策略折叠区 */}
        <div className="advanced-settings-section">
          <button className="advanced-toggle" onClick={() => setShowLegacy(!showLegacy)} type="button">
            <span className="toggle-icon">{showLegacy ? '▼' : '▶'}</span>
            More Strategies
          </button>
          {showLegacy && (
            <div className="optimization-cards legacy-cards">
              <div className="optimization-card">
                <h3>Maximize Elongation × UTS Product</h3>
                <p>Find alloys with maximum toughness (elongation × ultimate tensile strength) while satisfying density constraints.</p>
                <div className="card-params">
                  <div className="param-group">
                    <label>Maximum Density (g/cm³)</label>
                    <input type="number" value={optimizeElongationUtsParams.density_ub} onChange={e => handleElongationUtsParamChange('density_ub', e.target.value)} min="0" step="0.1" />
                  </div>
                </div>
                <button className={`card-button maximize-button ${isSearchingElongationUts ? 'searching-dots' : ''}`} onClick={handleOptimizeElongationUts} disabled={isSearchingElongationUts}>
                  {isSearchingElongationUts ? 'Searching' : 'Maximize Elongation × UTS'}
                </button>
              </div>

              <div className="optimization-card">
                <h3>Maximize Specific Strength</h3>
                <p>Find alloys with maximum specific strength (UTS/density) while satisfying elongation and density constraints.</p>
                <div className="card-params">
                  <div className="param-group">
                    <label>Minimum Elongation (%)</label>
                    <input type="number" value={optimizeSpecificStrengthParams.elongation_lb} onChange={e => handleSpecificStrengthParamChange('elongation_lb', e.target.value)} min="0" step="0.1" />
                  </div>
                  <div className="param-group">
                    <label>Maximum Density (g/cm³)</label>
                    <input type="number" value={optimizeSpecificStrengthParams.density_ub} onChange={e => handleSpecificStrengthParamChange('density_ub', e.target.value)} min="0" step="0.1" />
                  </div>
                </div>
                <button className={`card-button optimize-button ${isSearchingSpecificStrength ? 'searching-dots' : ''}`} onClick={handleOptimizeSpecificStrength} disabled={isSearchingSpecificStrength}>
                  {isSearchingSpecificStrength ? 'Searching' : 'Maximize Specific Strength'}
                </button>
              </div>
            </div>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        {densityResults && (
          <div className="results-section">
            {renderResultsTable(densityResults, `Top 5 Alloys with Minimum Density`)}
            {renderOptimizationChart(densityResults, 'Density Minimization Progress', 'density')}
          </div>
        )}
        {yieldStrengthResults && (
          <div className="results-section">
            {renderResultsTable(yieldStrengthResults, `Top 5 Alloys with Maximum Yield Strength`)}
            {renderOptimizationChart(yieldStrengthResults, 'Yield Strength Optimization Progress', 'yield_strength')}
          </div>
        )}
        {elongationUtsResults && (
          <div className="results-section">
            {renderResultsTable(elongationUtsResults, `Top 5 Alloys with Maximum Elongation × UTS Product`)}
            {renderOptimizationChart(elongationUtsResults, 'Elongation × UTS Product Optimization Progress', 'elongation_uts')}
          </div>
        )}
        {specificStrengthResults && (
          <div className="results-section">
            {renderResultsTable(specificStrengthResults, `Top 5 Alloys with Maximum Specific Strength`)}
            {renderOptimizationChart(specificStrengthResults, 'Specific Strength Optimization Progress', 'specific_strength')}
          </div>
        )}

        <div className={`designer-help${isHelpOpen ? '' : ' help-collapsed'}`}>
          <div className="help-header">
            <h3>How to use the designer</h3>
            <button
              type="button"
              className="help-toggle-button"
              onClick={() => setIsHelpOpen(prev => !prev)}
              aria-expanded={isHelpOpen}
            >
              {isHelpOpen ? 'Hide' : 'Show'}
            </button>
          </div>
          {isHelpOpen && (
            <div className="help-content">
              <div className="help-step"><div className="step-number">1</div><div className="step-content"><h4>Choose Optimization Strategy</h4><p>Select between density minimization, yield strength maximization, toughness maximization (elongation × UTS), or specific strength (UTS/density) maximization based on your application needs.</p></div></div>
              <div className="help-step"><div className="step-number">2</div><div className="step-content"><h4>Set Constraints</h4><p>Define your minimum/maximum constraints for each optimization strategy to ensure the resulting alloys meet your requirements.</p></div></div>
              <div className="help-step"><div className="step-number">3</div><div className="step-content"><h4>Optional: Advanced Settings</h4><p>Click "Advanced Settings" to adjust iterations and runs for more customized optimization.</p></div></div>
              <div className="help-step"><div className="step-number">4</div><div className="step-content"><h4>Analyze Results</h4><p>Review top-ranked compositions and optimization progress charts to understand the trade-offs and select the best alloy for your application.</p></div></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DesignerPage;
